import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../../design-system/primitives/Icon';
import Segmented from '../../design-system/primitives/Segmented';
import { apiService } from '../../services/api';
import {
  KEY_FILENAME,
  buildGuidedScript,
  detectOS,
  installCommandFor,
} from './bigqueryGuidedScript';
import {
  REDSHIFT_CREDS_FILENAME,
  awsInstallCommandFor,
  buildRedshiftGuidedScript,
} from './redshiftGuidedScript';

/**
 * Account → Connectors: an analysis-independent surface to manage every
 * warehouse connection the user owns. Add / test / refresh-catalog / delete,
 * and expand a connection to inspect its credentials summary, write status
 * and cached catalog (schemas → tables). Reuses the same REST endpoints the
 * in-analysis "Connect a data source" modal uses.
 */

const TYPES = [
  { key: 'snowflake', label: 'Snowflake', icon: 'snowflake' },
  { key: 'bigquery', label: 'BigQuery', icon: 'cloud' },
  { key: 'databricks', label: 'Databricks', icon: 'boxes' },
  { key: 'redshift', label: 'Amazon Redshift', icon: 'database-zap' },
];

const DOCS = {
  snowflake: 'docs/connectors/snowflake-setup.md',
  databricks: 'docs/connectors/databricks-setup.md',
  redshift: 'docs/connectors/redshift-setup.md',
};

const STATUS = {
  ok: { label: 'Connected', cls: 'ok', icon: 'circle-check' },
  error: { label: 'Error', cls: 'error', icon: 'circle-alert' },
  unverified: { label: 'Not verified', cls: 'idle', icon: 'circle-dashed' },
};

const typeMeta = (t) => TYPES.find((x) => x.key === t) || { label: t, icon: 'database' };

const schemaKey = (connId, name) => `${connId}::${name}`;

// Auto-expand schemas only for small catalogs; large ones start collapsed so
// many folders read as a compact list rather than one giant wall of tables.
const AUTO_EXPAND_TABLE_BUDGET = 15;
const TABLE_FILTER_THRESHOLD = 12; // show a search box once a schema has this many tables
const initialOpenSchemas = (connId, schemas) => {
  const open = new Set();
  const total = (schemas || []).reduce((n, s) => n + (s.tables ? s.tables.length : 0), 0);
  if ((schemas || []).length <= 1 || total <= AUTO_EXPAND_TABLE_BUDGET) {
    (schemas || []).forEach((s) => open.add(schemaKey(connId, s.name)));
  }
  return open;
};

const relTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const fmtRows = (n) => {
  if (n == null) return null;
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const ConnectorsPanel = () => {
  const [connections, setConnections] = useState(null);
  const [fieldSpecs, setFieldSpecs] = useState({});
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState('list'); // 'list' | 'create'
  const [createType, setCreateType] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // Guided setup (BigQuery / Redshift): default to the lowest-effort path.
  const [setupMode, setSetupMode] = useState('guided'); // 'guided' | 'manual'
  const [scriptOS, setScriptOS] = useState(detectOS()); // 'unix' | 'windows'
  const [scriptCopied, setScriptCopied] = useState(false);
  const [keyUpload, setKeyUpload] = useState(null); // { user } | { error }
  const [egress, setEgress] = useState(null); // { ips, stable } | null = not fetched yet

  const [busyId, setBusyId] = useState(null); // id of a connection with an in-flight action
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [catalogs, setCatalogs] = useState({}); // id -> { loading, error, schemas }
  const [openSchemas, setOpenSchemas] = useState(() => new Set()); // `${connId}::${schema}` accordion state
  const [tableFilter, setTableFilter] = useState({}); // `${connId}::${schema}` -> filter text

  const load = useCallback(() => {
    apiService.listConnections()
      .then((resp) => {
        setConnections(resp.data.connections || []);
        setFieldSpecs(resp.data.field_specs || {});
        setLoadError(null);
      })
      .catch(() => { setConnections([]); setLoadError('Could not load your connectors.'); });
  }, []);

  useEffect(() => { load(); }, [load]);

  // The Redshift script allowlists Qombra's egress IP in the customer's
  // security group — fetch it lazily, only when that flow is opened.
  useEffect(() => {
    if (view === 'create' && createType === 'redshift' && egress === null) {
      apiService.getWarehouseEgressIps()
        .then((resp) => setEgress({ ips: resp.data?.ips || [], stable: !!resp.data?.stable }))
        .catch(() => setEgress({ ips: [], stable: false }));
    }
  }, [view, createType, egress]);

  const handleCreate = useCallback(async () => {
    if (saving || !createType) return;
    setSaving(true);
    setFormError(null);
    try {
      await apiService.createConnection({ connection_type: createType, ...form });
      setForm({});
      setCreateType(null);
      setKeyUpload(null);
      setView('list');
      load();
    } catch (e) {
      // A failed test never persists a connection — just surface the error.
      setFormError(e?.response?.data?.detail || 'Could not create the connection.');
    } finally {
      setSaving(false);
    }
  }, [saving, createType, form, load]);

  const handleCopyScript = useCallback(async (script) => {
    if (!script) return;
    try {
      await navigator.clipboard.writeText(script);
      setScriptCopied(true);
      setTimeout(() => setScriptCopied(false), 2000);
    } catch { /* clipboard unavailable — the user can still select the text */ }
  }, []);

  const handleCredsFile = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    const isRedshift = createType === 'redshift';
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const parsed = JSON.parse(text);
        if (isRedshift) {
          if (!parsed.host || !parsed.user || !parsed.password) throw new Error('not a credentials file');
          setForm((f) => ({
            ...f,
            host: parsed.host,
            port: parsed.port || 5439,
            database: f.database || parsed.database || '',
            user: parsed.user,
            password: parsed.password,
          }));
          setKeyUpload({ user: `${parsed.user} @ ${parsed.host}` });
        } else {
          if (!parsed.client_email || !parsed.private_key) throw new Error('not a service account key');
          setForm((f) => ({
            ...f,
            service_account_json: text,
            project_id: f.project_id || parsed.project_id || '',
          }));
          setKeyUpload({ user: parsed.client_email });
        }
        setFormError(null);
      } catch {
        setKeyUpload({
          error: isRedshift
            ? `That file is not a valid Redshift credentials file. Upload the ${REDSHIFT_CREDS_FILENAME} the script saved.`
            : 'That file is not a valid service account key. Upload the JSON file the script saved.',
        });
      }
    };
    reader.readAsText(file);
  }, [createType]);

  const handleTest = useCallback(async (conn) => {
    setBusyId(conn.id);
    try { await apiService.testConnection(conn.id); }
    finally { setBusyId(null); load(); }
  }, [load]);

  const handleRefreshCatalog = useCallback(async (conn) => {
    setBusyId(conn.id);
    setCatalogs((c) => ({ ...c, [conn.id]: { ...(c[conn.id] || {}), loading: true, error: null } }));
    try {
      const resp = await apiService.refreshConnectionCatalog(conn.id);
      const schemas = resp.data?.catalog?.schemas || [];
      setCatalogs((c) => ({ ...c, [conn.id]: { loading: false, error: null, schemas } }));
      setOpenSchemas(initialOpenSchemas(conn.id, schemas));
      setExpandedId(conn.id);
    } catch (e) {
      setCatalogs((c) => ({ ...c, [conn.id]: { loading: false, error: e?.response?.data?.detail || 'Could not refresh the catalog.', schemas: [] } }));
    } finally {
      setBusyId(null);
      load();
    }
  }, [load]);

  const handleDelete = useCallback(async (conn) => {
    setBusyId(conn.id);
    try {
      await apiService.deleteConnection(conn.id);
      setConfirmDeleteId(null);
      if (expandedId === conn.id) setExpandedId(null);
      load();
    } finally {
      setBusyId(null);
    }
  }, [load, expandedId]);

  const toggleExpand = useCallback(async (conn) => {
    if (expandedId === conn.id) { setExpandedId(null); return; }
    setExpandedId(conn.id);
    if (catalogs[conn.id]?.schemas || catalogs[conn.id]?.loading) return;
    setCatalogs((c) => ({ ...c, [conn.id]: { loading: true, error: null, schemas: null } }));
    try {
      const resp = await apiService.getConnectionSchemas(conn.id);
      const schemas = resp.data?.catalog?.schemas || [];
      setCatalogs((c) => ({ ...c, [conn.id]: { loading: false, error: null, schemas } }));
      setOpenSchemas(initialOpenSchemas(conn.id, schemas));
    } catch (e) {
      setCatalogs((c) => ({ ...c, [conn.id]: { loading: false, error: e?.response?.data?.detail || 'Could not load the catalog.', schemas: [] } }));
    }
  }, [expandedId, catalogs]);

  const toggleSchema = useCallback((connId, name) => {
    const key = schemaKey(connId, name);
    setOpenSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  if (connections === null) {
    return <p className="account-empty">Loading connectors…</p>;
  }

  /* ---------------------------------------------------------------- */
  /* Create view                                                       */
  /* ---------------------------------------------------------------- */
  if (view === 'create') {
    const spec = createType ? fieldSpecs[createType] : null;
    const formFields = spec ? [...(spec.config || []), ...(spec.secrets || [])] : [];

    if (!createType) {
      return (
        <div>
          <div className="cn-create-head">
            <p className="account-section-desc" style={{ margin: 0 }}>Choose the warehouse you want to connect.</p>
            <button className="account-btn account-btn--ghost cn-back" onClick={() => setView('list')}>
              <Icon name="arrow-left" size={13} /> Back
            </button>
          </div>
          <div className="cn-type-grid">
            {TYPES.map((t) => (
              <button key={t.key} className="cn-type-card"
                onClick={() => {
                  setCreateType(t.key); setForm({}); setFormError(null);
                  setSetupMode('guided'); setKeyUpload(null);
                }}>
                <span className="cn-type-card__icon"><Icon name={t.icon} size={18} color="var(--accent)" /></span>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    const isBigQuery = createType === 'bigquery';
    const isRedshift = createType === 'redshift';
    const guided = (isBigQuery || isRedshift) && setupMode === 'guided';
    const projectId = (form.project_id || '').trim();
    const rsParams = {
      deployment: form.rs_deployment || 'provisioned',
      region: form.rs_region || '',
      identifier: form.rs_identifier || '',
      database: form.database || '',
      adminUser: form.rs_admin_user || 'awsuser',
    };
    const egressIp = egress?.ips?.[0] || '';
    // Redshift: wait for the egress IP lookup so nobody copies a script
    // that silently skips the security-group step.
    const script = !guided ? null
      : isBigQuery ? buildGuidedScript(projectId, scriptOS)
        : (egress !== null ? buildRedshiftGuidedScript(rsParams, scriptOS, egressIp) : null);
    const guidedCredsMissing = isBigQuery
      ? !form.service_account_json
      : !(form.host && form.user && form.password);

    const formActions = (
      <div className="account-form-actions">
        <button className="account-btn" onClick={handleCreate}
          disabled={saving || (guided && guidedCredsMissing)}>
          {saving && <Icon name="loader-2" size={14} className="cn-spin" />}
          {saving ? 'Testing…' : 'Test & save'}
        </button>
        <button className="account-btn account-btn--ghost" onClick={() => setView('list')} disabled={saving}>
          Cancel
        </button>
      </div>
    );

    return (
      <div>
        <div className="cn-create-head">
          <h3 className="account-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <Icon name={typeMeta(createType).icon} size={16} color="var(--accent)" />
            {typeMeta(createType).label}
          </h3>
          <button className="account-btn account-btn--ghost cn-back"
            onClick={() => { setCreateType(null); setFormError(null); setKeyUpload(null); }}>
            <Icon name="arrow-left" size={13} /> Back
          </button>
        </div>
        {isBigQuery ? (
          <p className="account-section-desc">
            Qombra can read all tables in this project, and can create tables prefixed{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>qombra_</span> and write only to those.
          </p>
        ) : isRedshift ? (
          <p className="account-section-desc">
            Qombra can read all schemas in this database, and can create tables prefixed{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>qombra_</span> and write only to those.
          </p>
        ) : (
          <p className="account-section-desc">
            Use a <strong>read-only</strong> role — Qombra never writes to your warehouse unless you
            explicitly configure a prediction destination in Manage Pipeline (that connection then also
            needs INSERT / CREATE TABLE rights on the target schema). Setup guide:{' '}
            <span style={{ fontFamily: 'var(--font-mono)' }}>{DOCS[createType]}</span>
          </p>
        )}

        {(isBigQuery || isRedshift) && (
          <div className="cn-tabs" role="tablist">
            <button role="tab" aria-selected={setupMode === 'guided'}
              className={`cn-tab ${setupMode === 'guided' ? 'cn-tab--active' : ''}`}
              onClick={() => { setSetupMode('guided'); setFormError(null); }}>
              <Icon name="wand-sparkles" size={13} /> Guided Automatic
            </button>
            <button role="tab" aria-selected={setupMode === 'manual'}
              className={`cn-tab ${setupMode === 'manual' ? 'cn-tab--active' : ''}`}
              onClick={() => { setSetupMode('manual'); setFormError(null); }}>
              <Icon name="settings-2" size={13} /> Manual
            </button>
          </div>
        )}

        {guided ? (
          <div className="account-form">
            {isBigQuery ? (
              <Field label="Google Cloud project ID" value={form.project_id || ''}
                placeholder="e.g. my-company-analytics"
                onChange={(v) => setForm((f) => ({ ...f, project_id: v }))} />
            ) : (
              <>
                <div className="account-field">
                  <span>Deployment</span>
                  <span className="cn-field-desc">
                    Are you connecting to a provisioned or a serverless Redshift data warehouse?
                  </span>
                  {/* block wrapper: keeps the inline-flex pill row content-width
                      instead of stretching to the column width */}
                  <div>
                    <Segmented value={rsParams.deployment}
                      onChange={(v) => setForm((f) => ({ ...f, rs_deployment: v }))}
                      options={[
                        { value: 'provisioned', label: 'Provisioned cluster' },
                        { value: 'serverless', label: 'Serverless' },
                      ]} />
                  </div>
                </div>
                <Field label="AWS region" value={form.rs_region || ''} placeholder="e.g. eu-central-1"
                  onChange={(v) => setForm((f) => ({ ...f, rs_region: v }))} />
                <Field
                  label={rsParams.deployment === 'serverless' ? 'Workgroup name' : 'Cluster identifier'}
                  value={form.rs_identifier || ''}
                  placeholder={rsParams.deployment === 'serverless' ? 'e.g. analytics-wg' : 'e.g. my-cluster'}
                  onChange={(v) => setForm((f) => ({ ...f, rs_identifier: v }))} />
                <Field label="Database" value={form.database || ''} placeholder="e.g. analytics"
                  onChange={(v) => setForm((f) => ({ ...f, database: v }))} />
                {rsParams.deployment === 'provisioned' && (
                  <Field label="Admin database user (runs the setup SQL)" value={form.rs_admin_user || ''}
                    placeholder="awsuser"
                    onChange={(v) => setForm((f) => ({ ...f, rs_admin_user: v }))} />
                )}
              </>
            )}

            {script && (
              <>
                <div className="cn-guided-note">
                  {isBigQuery ? (
                    <>Requires the Google Cloud CLI:{' '}
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{installCommandFor(scriptOS)}</span>, then{' '}
                      <span style={{ fontFamily: 'var(--font-mono)' }}>gcloud auth login</span>.</>
                  ) : (
                    <>Requires the AWS CLI:{' '}
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{awsInstallCommandFor(scriptOS)}</span>, signed
                      in as someone who can manage the cluster ({' '}
                      <span style={{ fontFamily: 'var(--font-mono)' }}>aws configure</span> or SSO).</>
                  )}
                </div>
                {isRedshift && (
                  <div className="cn-guided-note">
                    {egressIp ? (
                      <>Qombra connects from IP{' '}
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{egressIp}</span> — the script
                        allowlists it in your security group on the Redshift port.</>
                    ) : (
                      <>Qombra&apos;s egress IP could not be determined right now — the script will tell you
                        which security group needs a manual inbound rule.</>
                    )}
                  </div>
                )}
                <div className="cn-script-wrap">
                  <div className="cn-script-head">
                    <Segmented value={scriptOS} onChange={setScriptOS}
                      options={[{ value: 'unix', label: 'macOS / Linux' }, { value: 'windows', label: 'Windows' }]} />
                    <button className="cn-link-btn" onClick={() => handleCopyScript(script)}>
                      <Icon name={scriptCopied ? 'check' : 'copy'} size={12} />
                      {scriptCopied ? 'Copied' : 'Copy script'}
                    </button>
                  </div>
                  <pre className="cn-script">{script}</pre>
                </div>
                <p className="cn-guided-hint">
                  Paste this into your terminal{scriptOS === 'windows' ? ' (PowerShell)' : ''}. It creates a{' '}
                  <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {isBigQuery ? 'qombra-connector' : 'qombra_connector'}
                  </span>{' '}
                  {isBigQuery
                    ? 'service account and saves a key file to your Desktop. Then upload the key here:'
                    : 'database user (reads everywhere, writes only its own qombra_ tables), opens your security group to Qombra, and saves a credentials file to your Desktop. Then upload it here:'}
                </p>
                <label className={`cn-key-upload ${keyUpload?.user ? 'cn-key-upload--ok' : ''}`}>
                  <input type="file" accept=".json,application/json" style={{ display: 'none' }}
                    onChange={handleCredsFile} />
                  {keyUpload?.user ? (
                    <><Icon name="circle-check" size={15} color="var(--success, #22a06b)" />
                      <span>{isBigQuery ? 'Key' : 'Credentials'} loaded — {keyUpload.user}</span></>
                  ) : (
                    <><Icon name="upload" size={15} />
                      <span>Upload {isBigQuery ? `key file (${KEY_FILENAME}` : `credentials file (${REDSHIFT_CREDS_FILENAME}`} from your Desktop)</span></>
                  )}
                </label>
                {keyUpload?.error && <p className="account-error">{keyUpload.error}</p>}
                <Field label="Connection name" value={form.name || ''} placeholder="e.g. Production warehouse"
                  onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
              </>
            )}
            {!script && (isBigQuery ? projectId : (form.rs_region || form.rs_identifier || form.database)) && (
              <p className="cn-guided-hint">
                {isBigQuery
                  ? 'Enter a valid Google Cloud project ID to generate the setup script.'
                  : 'Fill in region, identifier and database (lowercase AWS names) to generate the setup script.'}
              </p>
            )}

            {formError && <p className="account-error">{formError}</p>}
            {script && formActions}
          </div>
        ) : (
          <div className="account-form">
            <Field label="Connection name" value={form.name || ''} placeholder="e.g. Production warehouse"
              onChange={(v) => setForm((f) => ({ ...f, name: v }))} />
            {formFields.map((f) => (
              <Field key={f.name}
                label={f.label + (f.required ? '' : ' (optional)')}
                value={form[f.name] || ''}
                multiline={f.multiline}
                secret={f.secret}
                placeholder={f.default != null ? String(f.default) : undefined}
                onChange={(v) => setForm((prev) => ({ ...prev, [f.name]: v }))} />
            ))}
            {formError && <p className="account-error">{formError}</p>}
            {formActions}
          </div>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* List view                                                         */
  /* ---------------------------------------------------------------- */
  return (
    <div>
      <div className="cn-head">
        <p className="account-section-desc" style={{ margin: 0 }}>
          Warehouse connections are shared across all your analyses. Manage credentials, verify
          connectivity and browse what each one exposes.
        </p>
        <button className="account-btn" onClick={() => { setView('create'); setCreateType(null); setFormError(null); }}>
          <Icon name="plus" size={14} /> Add connector
        </button>
      </div>

      {loadError && <p className="account-error" style={{ marginBottom: 16 }}>{loadError}</p>}

      {connections.length === 0 ? (
        <div className="cn-empty">
          <div className="cn-empty__icon"><Icon name="database" size={26} color="var(--fg-subtle)" /></div>
          <p className="cn-empty__title">No connectors yet</p>
          <p className="cn-empty__desc">Connect Snowflake, BigQuery, Databricks or Redshift to pull data and write predictions back.</p>
        </div>
      ) : (
        <div className="cn-list">
          {connections.map((c) => {
            const meta = typeMeta(c.connection_type);
            const st = STATUS[c.status] || STATUS.unverified;
            const busy = busyId === c.id;
            const expanded = expandedId === c.id;
            const cat = catalogs[c.id];
            const writeEnabled = !!(c.config && c.config.allow_writes);
            return (
              <div key={c.id} className={`cn-card ${expanded ? 'cn-card--open' : ''}`}>
                <div className="cn-card__main">
                  <span className="cn-card__type-icon"><Icon name={meta.icon} size={18} color="var(--accent)" /></span>
                  <div className="cn-card__body">
                    <div className="cn-card__title-row">
                      <span className="cn-card__name">{c.name}</span>
                      <span className={`cn-badge cn-badge--${st.cls}`}>
                        <Icon name={st.icon} size={12} /> {st.label}
                      </span>
                      {writeEnabled && (
                        <span className="cn-badge cn-badge--write" title="A prediction destination writes to this connection">
                          <Icon name="pencil" size={11} /> Write-enabled
                        </span>
                      )}
                    </div>
                    <div className="cn-card__meta">
                      <span>{meta.label}</span>
                      {c.credential_summary?.auth && <><span className="cn-dot" /><span>{c.credential_summary.auth}</span></>}
                      {c.credential_summary?.user && <><span className="cn-dot" /><span>{c.credential_summary.user}</span></>}
                      {c.status === 'ok' && c.last_verified_at && (
                        <><span className="cn-dot" /><span>verified {relTime(c.last_verified_at)}</span></>
                      )}
                    </div>
                    {c.status === 'error' && c.last_error && (
                      <div className="cn-card__error">{c.last_error}</div>
                    )}
                  </div>
                  <div className="cn-card__actions">
                    {c.status === 'ok' && (
                      <button className="cn-icon-btn" title={expanded ? 'Hide catalog' : 'Browse catalog'}
                        onClick={() => toggleExpand(c)} disabled={busy}>
                        <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={16} />
                      </button>
                    )}
                    <button className="cn-icon-btn" title={c.status === 'ok' ? 'Re-test' : 'Test connection'}
                      onClick={() => handleTest(c)} disabled={busy}>
                      <Icon name={busy ? 'loader-2' : 'plug-zap'} size={15} className={busy ? 'cn-spin' : undefined} />
                    </button>
                    {confirmDeleteId === c.id ? (
                      <span className="cn-confirm">
                        <button className="cn-confirm__yes" onClick={() => handleDelete(c)} disabled={busy}>Remove</button>
                        <button className="cn-confirm__no" onClick={() => setConfirmDeleteId(null)} disabled={busy}>Cancel</button>
                      </span>
                    ) : (
                      <button className="cn-icon-btn cn-icon-btn--danger" title="Remove connector"
                        onClick={() => setConfirmDeleteId(c.id)} disabled={busy}>
                        <Icon name="trash-2" size={15} />
                      </button>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="cn-catalog">
                    <div className="cn-catalog__head">
                      <span className="cn-catalog__title">
                        Catalog{c.catalog_cached_at ? ` · cached ${relTime(c.catalog_cached_at)}` : ''}
                      </span>
                      <button className="cn-link-btn" onClick={() => handleRefreshCatalog(c)} disabled={busy}>
                        <Icon name="refresh-cw" size={12} className={busy ? 'cn-spin' : undefined} /> Refresh
                      </button>
                    </div>
                    {cat?.loading && <p className="cn-catalog__hint">Loading catalog…</p>}
                    {cat?.error && <p className="account-error" style={{ marginTop: 4 }}>{cat.error}</p>}
                    {!cat?.loading && !cat?.error && cat?.schemas && cat.schemas.length === 0 && (
                      <p className="cn-catalog__hint">No schemas visible to this connection.</p>
                    )}
                    {!cat?.loading && cat?.schemas?.length > 0 && (
                      <div className="cn-catalog__scroll">
                        {cat.schemas.map((s) => {
                          const key = schemaKey(c.id, s.name);
                          const open = openSchemas.has(key);
                          const tables = s.tables || [];
                          const showFilter = open && tables.length > TABLE_FILTER_THRESHOLD;
                          const q = (tableFilter[key] || '').trim().toLowerCase();
                          const shown = q ? tables.filter((t) => t.name.toLowerCase().includes(q)) : tables;
                          return (
                            <div key={s.name} className="cn-schema">
                              <button className="cn-schema__hdr" onClick={() => toggleSchema(c.id, s.name)}>
                                <Icon name={open ? 'chevron-down' : 'chevron-right'} size={14} color="var(--fg-subtle)" />
                                <Icon name="folder" size={13} color="var(--fg-subtle)" />
                                <span className="cn-schema__name">{s.name}</span>
                                <span className="cn-schema__count">
                                  {tables.length}{s.truncated ? '+' : ''} table{tables.length === 1 ? '' : 's'}
                                </span>
                              </button>
                              {open && (s.error ? (
                                <p className="cn-catalog__hint cn-catalog__hint--indent">{s.error}</p>
                              ) : (
                                <>
                                  {showFilter && (
                                    <input
                                      className="cn-table-filter"
                                      placeholder={`Filter ${tables.length} tables…`}
                                      value={tableFilter[key] || ''}
                                      onChange={(e) => setTableFilter((f) => ({ ...f, [key]: e.target.value }))}
                                    />
                                  )}
                                  <div className="cn-table-list">
                                    {shown.map((t) => (
                                      <div key={t.name} className="cn-table">
                                        <span className="cn-table__name"><Icon name="table" size={12} color="var(--fg-subtle)" /> {t.name}</span>
                                        {fmtRows(t.rows) != null && <span className="cn-table__rows">{fmtRows(t.rows)} rows</span>}
                                      </div>
                                    ))}
                                    {shown.length === 0 && q && (
                                      <p className="cn-catalog__hint cn-catalog__hint--indent">No tables match “{tableFilter[key]}”.</p>
                                    )}
                                    {!q && s.truncated && (
                                      <p className="cn-catalog__hint cn-catalog__hint--indent">…more tables not shown — import narrows this by query.</p>
                                    )}
                                  </div>
                                </>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, multiline, secret, placeholder }) => (
  <label className="account-field">
    <span>{label}</span>
    {multiline ? (
      <textarea className="account-input cn-textarea" value={value} rows={4} spellCheck={false}
        placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className="account-input" type={secret ? 'password' : 'text'} value={value}
        placeholder={placeholder} autoComplete="off" onChange={(e) => onChange(e.target.value)} />
    )}
  </label>
);

export default ConnectorsPanel;
